import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  TrendingUp,
  Boxes,
  AlertTriangle,
  ArrowUpDown,
  RefreshCcw,
  Plus,
} from "lucide-react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import PostMovementModal from "./PostMovementModal";
import InventoryItemFlowTable from "./InventoryItemFlowTable";
import "./inventoryDashboard.css";

import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function iso(d) {
  return d.toISOString().slice(0, 10);
}

export default function InventoryDashboard() {
  const { companyId } = useCompany();
  const [balances, setBalances] = useState([]);
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [openPost, setOpenPost] = useState(false);

  const pdfRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;

    const { data: it } = await supabase
      .from("inventory_items")
      .select(
        "id,sku,name,unit,buffer_stock,reorder_level,sales_price,is_active"
      )
      .eq("company_id", companyId)
      .eq("is_active", true);

    setItems(it || []);

    const { data: b } = await supabase
      .from("inventory_balances")
      .select("warehouse_id,item_id,qty_on_hand,avg_cost")
      .eq("company_id", companyId);

    setBalances(b || []);

    const d0 = new Date();
    d0.setDate(d0.getDate() - 29);
    const from = iso(d0);
    const to = iso(new Date());

    const { data: mv } = await supabase
      .from("inventory_movements")
      .select("id,movement_type,movement_date")
      .eq("company_id", companyId)
      .gte("movement_date", from)
      .lte("movement_date", to);

    setMovements(mv || []);
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const itemMap = useMemo(
    () => new Map((items || []).map((i) => [i.id, i])),
    [items]
  );

  const kpis = useMemo(() => {
    const totalValue = (balances || []).reduce(
      (s, r) => s + Number(r.qty_on_hand || 0) * Number(r.avg_cost || 0),
      0
    );

    // Low stock based on sum across warehouses (simple SME model)
    const byItemQty = new Map();
    (balances || []).forEach((r) => {
      const cur = byItemQty.get(r.item_id) || 0;
      byItemQty.set(r.item_id, cur + Number(r.qty_on_hand || 0));
    });

    let lowCount = 0;
    let totalSku = 0;
    for (const [itemId, qty] of byItemQty.entries()) {
      const it = itemMap.get(itemId);
      if (!it) continue;
      totalSku += 1;
      const threshold = Math.max(
        Number(it.buffer_stock || 0),
        Number(it.reorder_level || 0)
      );
      if (threshold > 0 && qty <= threshold) lowCount += 1;
    }

    return {
      totalValue,
      lowCount,
      totalSku,
    };
  }, [balances, itemMap]);

  const movementTrend = useMemo(() => {
    // last 30 days buckets
    const days = [];
    const counts = [];
    const inCounts = [];
    const outCounts = [];
    const adjCounts = [];
    const trCounts = [];

    const start = new Date();
    start.setDate(start.getDate() - 29);

    const map = new Map();
    (movements || []).forEach((m) => {
      const k = m.movement_date;
      const cur = map.get(k) || { IN: 0, OUT: 0, ADJUSTMENT: 0, TRANSFER: 0 };
      cur[m.movement_type] = (cur[m.movement_type] || 0) + 1;
      map.set(k, cur);
    });

    for (let i = 0; i < 30; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const k = iso(d);
      const c = map.get(k) || { IN: 0, OUT: 0, ADJUSTMENT: 0, TRANSFER: 0 };
      days.push(k.slice(5)); // MM-DD
      inCounts.push(c.IN);
      outCounts.push(c.OUT);
      adjCounts.push(c.ADJUSTMENT);
      trCounts.push(c.TRANSFER);
      counts.push(c.IN + c.OUT + c.ADJUSTMENT + c.TRANSFER);
    }

    return { days, counts, inCounts, outCounts, adjCounts, trCounts };
  }, [movements]);

  const topByValue = useMemo(() => {
    const sumByItem = new Map();
    (balances || []).forEach((r) => {
      const v = Number(r.qty_on_hand || 0) * Number(r.avg_cost || 0);
      sumByItem.set(r.item_id, (sumByItem.get(r.item_id) || 0) + v);
    });

    const arr = [...sumByItem.entries()]
      .map(([item_id, value]) => ({ item_id, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const labels = arr.map((x) => itemMap.get(x.item_id)?.name || "Unknown");
    const values = arr.map((x) => Number(x.value.toFixed(2)));

    return { labels, values, raw: arr };
  }, [balances, itemMap]);

  const lineData = useMemo(() => {
    return {
      labels: movementTrend.days,
      datasets: [
        { label: "All movements", data: movementTrend.counts },
        { label: "IN", data: movementTrend.inCounts },
        { label: "OUT", data: movementTrend.outCounts },
      ],
    };
  }, [movementTrend]);

  const barData = useMemo(() => {
    return {
      labels: topByValue.labels,
      datasets: [{ label: "Value", data: topByValue.values }],
    };
  }, [topByValue]);

  async function exportDashboardPDF() {
    if (!pdfRef.current) return;
    setPdfBusy(true);

    try {
      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // image aspect -> fit width
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight; // negative offset
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(
        `inventory_dashboard_${new Date().toISOString().slice(0, 10)}.pdf`
      );
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <div className="fa-wrap" ref={pdfRef}>
      <div className="fa-header">
        <div className="fa-title">
          <h2>Inventory Dashboard</h2>
          <p>
            Quick insights: stock value, low stock alerts, and movement
            activity.
          </p>
        </div>

        <div className="fa-actions">
          <button className="fa-btn" onClick={load}>
            <RefreshCcw size={16} /> Refresh
          </button>
          <button
            className="fa-btn fa-btn-primary"
            onClick={() => setOpenPost(true)}
          >
            <Plus size={16} /> Post Movement
          </button>
          <button
            className="fa-btn"
            onClick={exportDashboardPDF}
            disabled={pdfBusy}
          >
            {pdfBusy ? "Exporting..." : "Export PDF"}
          </button>
        </div>
      </div>

      <div className="fa-split">
        <div className="fa-panel">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            <div className="fa-card">
              <div className="label">Total Stock Value</div>
              <div className="value">{kpis.totalValue.toFixed(2)}</div>
              <div className="hint">
                <TrendingUp size={14} style={{ marginRight: 6 }} />
                Qty*Avg cost
              </div>
            </div>

            <div className="fa-card">
              <div className="label">Active SKUs</div>
              <div className="value">{kpis.totalSku}</div>
              <div className="hint">
                <Boxes size={14} style={{ marginRight: 6 }} />
                Trackable items
              </div>
            </div>

            <div className="fa-card">
              <div className="label">Low Stock SKUs</div>
              <div className="value">{kpis.lowCount}</div>
              <div className="hint">
                <AlertTriangle size={14} style={{ marginRight: 6 }} />
                At/below buffer
              </div>
            </div>

            <div className="fa-card">
              <div className="label">30-day Movements</div>
              <div className="value">
                {movementTrend.counts.reduce((a, b) => a + b, 0)}
              </div>
              <div className="hint">
                <ArrowUpDown size={14} style={{ marginRight: 6 }} />
                IN/OUT/ADJ/TR
              </div>
            </div>
          </div>

          <div className="fa-panel" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>
              Movements Trend (Last 30 days)
            </div>
            <div className="inv-trend-chart">
              <Line
                data={lineData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                }}
              />
            </div>
          </div>

          <div className="fa-panel" style={{ marginTop: 12 }}>
            <InventoryItemFlowTable
              companyId={companyId}
              items={items}
              balances={balances}
            />
          </div>
        </div>

        <div className="fa-panel">
          <div style={{ fontWeight: 900, marginBottom: 10 }}>
            Top Items by Value
          </div>
          <div style={{ height: 320 }}>
            <Bar
              data={barData}
              options={{ responsive: true, maintainAspectRatio: false }}
            />
          </div>

          <div className="fa-panel" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>
              Top 10 Table
            </div>
            <table className="fa-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th style={{ width: 160 }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {topByValue.raw.map((r) => (
                  <tr key={r.item_id}>
                    <td style={{ fontWeight: 900 }}>
                      {itemMap.get(r.item_id)?.name || "Unknown"}
                    </td>
                    <td>{Number(r.value).toFixed(2)}</td>
                  </tr>
                ))}
                {topByValue.raw.length === 0 && (
                  <tr>
                    <td colSpan={2} style={{ opacity: 0.75 }}>
                      No balances yet. Post Stock IN to start.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {openPost && (
        <PostMovementModal
          onClose={() => setOpenPost(false)}
          onPosted={() => {
            setOpenPost(false);
            load();
          }}
        />
      )}
    </div>
  );
}
